import type { RouteGuardConfig } from '@connected/core';

import type { ElektraConditionContext } from '@/config/conditions';

import { elektraConditions } from '@/config/conditions';

export { getRedirectUrlFromResult } from '@connected/api-client/lib/sso';
// Re-export utilities for use with the route guard
export { checkRouteGuard, isPathAllowed } from '@connected/core';

/**
 * Get the fallback route for redirecting to portaal
 *
 * If user has an organization, redirect to their projects list
 * Otherwise, redirect to the organizations page
 */
function getFallbackRoute(context: ElektraConditionContext): string {
    const user = context.currentUser as { Organization?: { OrganizationId?: string } } | null;
    const organizationId = user?.Organization?.OrganizationId;

    if (organizationId) {
        return `/dashboard/organizations/${organizationId}/projects`;
    }

    return '/dashboard/organizations';
}

/**
 * Route guard configuration for elektra-app using the unified condition system.
 *
 * Routes are allowed if ANY of these conditions pass:
 * - canAccessApp: authenticated user on a /projects/{uuid}/** path
 * - canAccessSso: SSO callback routes (/sso/*)
 * - canAccessLogin: Login routes (/login/*)
 *
 * If no conditions pass, user is redirected to portaal via SSO.
 *
 * @example
 * // Check route access:
 * const result = checkRouteGuard({
 *     config: elektraRouteGuard,
 *     context: conditionContext,
 *     conditions: elektraConditions,
 * });
 *
 * if (!result.allowed) {
 *     const url = getRedirectUrlFromResult(result);
 *     window.location.href = url;
 * }
 */
export const elektraRouteGuard: RouteGuardConfig<ElektraConditionContext> = {
    allowedConditions: ['canAccessApp', 'canAccessSso', 'canAccessLogin'],
    getFallbackRoute,
    targetDomain: 'portaal',
};

/**
 * The conditions map for use with checkRouteGuard.
 * This is the same conditions object exported from @/config/conditions.
 */
export { elektraConditions };
